import { iso6393 } from 'iso-639-3'
import relation from './relation'
import { dialectToCodeMap, codeToDialectMap } from './dialects'
import { getFlagSvgUrl } from './flagSvgs'

const savedLanguage = localStorage.getItem('selectedLanguage')

const lookupMapCache = new Map()

function toTitleCase(word) {
  if (!word) return ''
  // Keep existing casing after the first letter so names with parentheses
  // (e.g., "Serbian (Latin)") are preserved.
  return word.charAt(0).toUpperCase() + word.slice(1)
}

function createLanguageNameToCodeMap(
  codeType = 'iso6391',
  mappedLanguage = 'en'
) {
  const displayNames = new Intl.DisplayNames([mappedLanguage], {
    type: 'language',
  })
  const map = new Map()

  for (const lang of iso6393) {
    if (lang['iso6391']) {
      if (lang[codeType]) {
        const localizedName = displayNames.of(lang[codeType])
        if (localizedName) {
          map.set(localizedName.toLowerCase(), lang[codeType])
        }
      }
    }
  }

  return map
}

function getCodeFromLanguageName(languageName, codeType = 'iso6391') {
  const currentLanguage = savedLanguage ?? 'en'
  const cacheKey = `${codeType}_${currentLanguage}`

  let lookupMap = lookupMapCache.get(cacheKey)

  if (!lookupMap) {
    const languageMap = createLanguageNameToCodeMap(codeType, currentLanguage)
    lookupMap = new Map([...languageMap, ...dialectToCodeMap])
    lookupMapCache.set(cacheKey, lookupMap)
  }

  return lookupMap.get(languageName.toLowerCase()) || null
}

export function getLanguageName(code) {
  if (!code || typeof code !== 'string' || code.trim() === '') {
    return ''
  }

  try {
    if (codeToDialectMap.has(code)) {
      return codeToDialectMap.get(code)
    }
    const displayNames = new Intl.DisplayNames([savedLanguage], {
      type: 'language',
    })
    return toTitleCase(displayNames.of(code))
  } catch {
    return code.toUpperCase()
  }
}

export function getIso6391Code(languageName) {
  return getCodeFromLanguageName(languageName, 'iso6391')
}

function getFlagIconClass(languageName, detectLabel = 'Detect') {
  try {
    if (languageName !== null && languageName !== detectLabel) {
      const flagCode = relation[getCodeFromLanguageName(languageName)]
      if (!flagCode) {
        const normalizedName =
          typeof languageName === 'string'
            ? languageName.toLowerCase()
            : languageName
        if (dialectToCodeMap.has(normalizedName)) {
          return relation[dialectToCodeMap.get(normalizedName)]
        }
        return ''
      }
      return `${flagCode}`
    }
  } catch {
    return ''
  }
}

/**
 * Returns the asset URL for the flag SVG matching the given language name.
 * Use this to render flags as <img> elements instead of CSS background classes,
 * so that only the flags for visible languages are fetched by the browser.
 *
 * @param {string} languageName - Display name of the language (e.g. "English")
 * @param {string} [detectLabel] - Label used for the "Detect" option; skipped
 * @returns {string} URL string, or empty string if no flag is available
 */
export function getFlagUrl(languageName, detectLabel = 'Detect') {
  const countryCode = getFlagIconClass(languageName, detectLabel)
  return getFlagSvgUrl(countryCode)
}

export function getIso6393Code(languageName) {
  if (languageName) return getCodeFromLanguageName(languageName, 'iso6393')
}

export function getLanguageOwnName(code) {
  if (!code) {
    return ''
  }
  const displayNames = new Intl.DisplayNames([code], { type: 'language' })
  return toTitleCase(displayNames.of(code))
}

export function toggleCodes(code) {
  if (!code) return null

  if (code.length === 2) return getIso6393Code(getLanguageName(code))
  else if (code.length === 3) return getIso6391Code(getLanguageName(code))
  else return code
}

// Languages rendered right-to-left. Kept in sync with the languages wired
// into flagSvgs.js/relation.js/keyboardLayouts.js/useSTT.ts, not a general
// RTL-script list, since no unsupported language should hit this path.
const RTL_LANGUAGE_CODES = new Set(['ar', 'he'])

export function isRtlLanguage(code) {
  if (!code || typeof code !== 'string') {
    return false
  }
  const baseLanguage = code.trim().toLowerCase().split('-')[0]
  return RTL_LANGUAGE_CODES.has(baseLanguage)
}

export function compareLanguageCodes(code1, code2) {
  // Handle null/undefined cases
  if (!code1 || !code2) {
    return code1 === code2 // Returns true if both are falsy, false otherwise
  }

  if (code1.length === 2) {
    code1 = toggleCodes(code1)
  }
  if (code2.length === 2) {
    code2 = toggleCodes(code2)
  }
  return code1 === code2
}

/**
 * Strips a region/script subtag (e.g. "en-GB" -> "en", "zh-Hans" -> "zh"),
 * leaving the base language code untouched otherwise.
 */
export function getBaseLanguageCode(code) {
  if (!code) return code
  return code.includes('-') ? code.split('-')[0] : code
}

/**
 * Whether two language codes refer to the same base language, tolerant of
 * 2-vs-3-letter codes and region/script subtags. Returns false if either
 * code is missing (use this to require both sides, callers decide what a
 * missing code means for "no constraint" semantics).
 */
export function languageCodesMatch(code1, code2) {
  if (!code1 || !code2) return false
  return compareLanguageCodes(
    getBaseLanguageCode(code1),
    getBaseLanguageCode(code2)
  )
}
